import React, {FC, useMemo, useRef, useState} from "react";
import SCModal from "@/PageComponents/Modal/SCModal";
import * as Enums from "@/utils/enums";
import {useQuery} from "@tanstack/react-query";
import Fetch from "@/utils/Fetch";
import EmployeeService from "@/services/employee/employee-service";
import DocumentService from "@/services/document/document-service";
import {Box, Button, CloseButton, Flex, Group, Loader, LoadingOverlay, Modal, ScrollArea, Text} from "@mantine/core";
import NewMessageForm from "@/PageComponents/Message/Communication/NewMessageForm";
import {IconSend} from "@tabler/icons-react";
import {useOverlayLayer} from "@/utils/atoms";

interface NewMessageFormModalProps {
    onClose: () => void;
    onSent: () => void;
    show: boolean;
    id: string;
    method: 'email' | 'sms' | 'both';
    module: number;
    templateID?: string;
    attachQuote?: boolean;
    attachInvoice?: boolean;
    attachPurchaseOrder?: boolean;
    attachJobCard?: boolean;
    preSelectEmployeeId?: string;
    initialValues?: any;
}

const getItem = (module: number, id: string) => {
    let itemUrl = ''
    switch (module) {
        case Enums.Module.Customer:
            itemUrl = '/Customer/';
            break;
        case Enums.Module.JobCard:
            itemUrl = '/Job/';
            break;
        case Enums.Module.Asset:
            itemUrl = '/Product?id=';
            break;
        case Enums.Module.Query:
            itemUrl = '/Query/';
            break;
        case Enums.Module.Quote:
            itemUrl = '/Quote/';
            break;
        case Enums.Module.Invoice:
            itemUrl = '/Invoice/';
            break;
        case Enums.Module.Project:
            itemUrl = '/Project/';
            break;
        case Enums.Module.Supplier:
            itemUrl = '/Supplier/';
            break;
        case Enums.Module.PurchaseOrder:
            itemUrl = '/PurchaseOrder/';
            break;
        default:
            break;
    }
    return Fetch.get({
        url: itemUrl + id,
    })
}

const getOtherProps = async (item: any, isSupplier: boolean, isPurchaseOrder: boolean, moduleCode: number, templateID?: string) => {

    let customer: any = null;
    let supplier: any = null;

    if (isSupplier) {
        supplier = item;
        if (isPurchaseOrder) {
            supplier = await Fetch.get({
                url: `/Supplier/${item.SupplierID}`,
            } as any);
        }
    } else {
        customer = item;
        if (moduleCode)
            if (moduleCode != 0) {
                customer = await Fetch.get({
                    url: '/Customer/' + item.CustomerID,
                });
            }
    }

    const templates = await Fetch.post({
        url: '/Template/GetTemplates',
        params: {
            ModuleList: [Enums.getEnumStringValue(Enums.Module, moduleCode)],
            PageSize: 1000
        },
    });

    let template: any = null;
    if (templateID) {
        // get template to pass to component to preselect
        const templateResult = await Fetch.get({
            url: '/Template',
            params: { id: templateID },
        });
        template = templateResult;
    }

    const employees = await EmployeeService.getEmployees(item.StoreID);

    let documentDefinitionMetaData = null;
    const legacyDocuments = await DocumentService.getUseLegacyDocuments();
    if (!legacyDocuments) {
        const documentDefinition = await DocumentService.getDocumentDefinition();
        if (documentDefinition && documentDefinition.MetaData !== undefined) {
            documentDefinitionMetaData = JSON.parse(documentDefinition.MetaData);
        }
    }

    return {
        supplier,
        customer,
        moduleCode,
        templates: templates.Results,
        template,
        employees: employees.Results,
        documentDefinitionMetaData: documentDefinitionMetaData
    };
}

const NewMessageFormModal: FC<NewMessageFormModalProps> = ({onClose, show, id: itemId, ...props}) => {

    // registers in the shared overlay stack so a drawer underneath (quote/invoice/PO) doesn't also close on Escape
    useOverlayLayer(!!show)

    const { module, templateID } = props;
    const formRef = useRef<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [messageType, setMessageType] = useState<number>(
        props.method === 'sms' ? Enums.MessageType.SMS :
            props.method === 'both' ? Enums.MessageType.Both :
                Enums.MessageType.Email
    );

    const buttonText = useMemo(() => {
        const emailActive = messageType === Enums.MessageType.Email || messageType === Enums.MessageType.Both;
        const smsActive = messageType === Enums.MessageType.SMS || messageType === Enums.MessageType.Both;
        if (emailActive && smsActive) return "Send Email and SMS";
        if (emailActive) return "Send Email";
        if (smsActive) return "Send SMS";
        return "Send";
    }, [messageType]);

    const {data: item, isFetching} = useQuery(['newCommunicationItem', itemId], () => getItem(module, itemId as string),
        {
            enabled: !!itemId && show,
        })

    const {data: otherProps, isSuccess: dataLoaded, isFetching: dataFetching} = useQuery(
        ['itemCommunicationData', item?.ID],
        () => getOtherProps(
            item,
            module === Enums.Module.PurchaseOrder || module === Enums.Module.Supplier,
            module === Enums.Module.PurchaseOrder,
            module,
            templateID
        ),
        {
            enabled: !!item?.ID && !isFetching
        }
    )

    const [validateAndCloseCounter, setValidateAndClose] = useState(0)

    const close = () => {
        ((!dataLoaded || dataFetching || isFetching) && onClose && onClose()) ||
        setValidateAndClose((p: any) => p + 1)
    }

    const headerSection = (
        <Flex
            w={'100%'}
            align={'center'}
            px={'lg'}
            py={'xs'}
            bg={'gray.0'}
            pos={'sticky'}
            top={0}
            style={{borderBottom: '1px solid var(--mantine-color-gray-3)', zIndex: 1010}}
        >
            <Text fw={700} size={'md'} c={'dark.7'}>Compose Message</Text>
            <Group ml={'auto'} gap={'sm'}>
                <Button
                    onClick={close}
                    variant={'default'}
                >
                    Cancel
                </Button>
                <Button
                    onClick={() => formRef.current?.submit()}
                    leftSection={isLoading ? <Loader color={'scBlue'} size={18}/> : <IconSend/>}
                    disabled={isLoading}
                >
                    {buttonText}
                </Button>
            </Group>
        </Flex>
    );

    return <>
        <Modal
            size={1200}
            opened={show}
            withCloseButton={false}
            p={0}
            onClose={close}
            title={headerSection}
            styles={{
                header: {
                    padding: 0,
                    width: '100%',
                    zIndex: 1010,
                },
                title: {
                    padding: 0,
                    width: '100%',
                }
            }}
        >
            {
                dataLoaded && !dataFetching && !isFetching && <>
                    <NewMessageForm
                        ref={formRef}
                        item={item}
                        itemId={itemId}
                        {...props}
                        {...otherProps}
                        initialValues={ props.initialValues}
                        onClose={onClose}
                        validateAndCloseCounter={validateAndCloseCounter}
                        withBorder={false}
                        hideHeader
                        emailMaxHeight={450}
                        onLoading={setIsLoading}
                        onMessageTypeChange={setMessageType}
                    />
                </>
                ||
                <Box mih={700}>
                    <LoadingOverlay visible/>
                </Box>

            }
        </Modal>
    </>;
}

export default NewMessageFormModal;
